import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 默认内容（如果API获取失败时使用）
const defaultIntroSections = [
  {
    id: 1,
    title: "3C基督徒联盟是什么？",
    descriptions: [
      "它是一个线上与线下相结合的基督徒生活与事奉的平台；",
      "在这里，有志服侍的弟兄姐妹，可以找到合适的人群来进行可持续的事奉，也可以得到平台的支持和牧养；",
      "在这里，希望得到牧养的弟兄姐妹，可以获得真理正统、生命敬虔的服侍，更可以与其他弟兄姐妹一起清楚自己的呼召，发现和培养自己的恩赐，参与到事奉中来；",
      "在这里，将没有时间的阻隔、地域的阻隔、也将没有资源的局限、教派的局限。",
    ],
  },
  {
    id: 2,
    title: "3C基督徒联盟的异象是什么？",
    descriptions: ["我们的异象是人人领受异象；每个人都清楚自己的召命是什么。"],
  },
  {
    id: 3,
    title: "3C基督徒联盟的策略是什么？",
    descriptions: [
      "1、人人领受呼召（Calling）",
      "2、平台连接你我（Connecting）",
      "3、教会关顾众人（Caring）",
    ],
  },
  {
    id: 4,
    title: "3C基督徒联盟的认信信条是什么？",
    descriptions: [
      "我们相信新旧约圣经全是 神的启示，完全由圣灵感动作者写成，原本毫无错误，是信仰与生活的最高准则。",
      "我们相信独一真神是永存的圣父、圣子、圣灵，创造、统管并扶持万有的主宰。",
      "我们相信耶稣基督是 神的独生子，由圣灵感孕童女马里亚所生，舍身流血为世人赎罪；衪从死里复活升天，并照衪的应许将再降临。",
      "我们相信圣灵与圣父、圣子同质、同权、同存，衪在教会中运行、同工、引导信者进入真理。",
      "我们相信人是照神的形像造成的，在亚当里堕落成为罪人，需要神的救恩。",
      "我们相信悔改的罪人，接受耶稣始得重生，并因信仰耶稣基督得以称义。",
      "我们相信凡称义的人能因圣灵成圣，活出圣洁的生活。",
      "我们相信信者身体复活得永生，不信之人复活受永刑。",
      "我们相信教会是一体，基督是元首，由圣灵重生者所组成，应在敬拜、交通及事奉上彼此相通。",
      "我们相信家庭是上帝所看重的，是事奉上帝的基本单位（约书亚记24:15），婚姻是神宣告为一男一女在盟约承诺中的联合，合法的性关系是仅限于婚姻关系以内。我们相信儿女是上帝的赐福，更是托付，他们对上帝的认识和跟随是我们的第一养育责任。",
    ],
  },
];

// Section容器 - 企业化设计
const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const MobileIntro = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [introSections, setIntroSections] = useState(defaultIntroSections);

  useEffect(() => {
    const fetchIntroContent = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/intro-content`);
        if (response.data && response.data.length > 0) {
          const formattedSections = response.data
            .sort((a, b) => a.sectionId - b.sectionId)
            .map((item) => ({
              id: item.sectionId,
              title: item.title.replace(/^\d+\.\s*/, ""), // 移除数字前缀
              descriptions: item.descriptions,
            }));
          setIntroSections(formattedSections);
        }
      } catch (err) {
        console.error("获取简介内容失败:", err);
        setIntroSections(defaultIntroSections);
      }
    };

    fetchIntroContent();
  }, []);

  const textColor = prefersDarkMode ? "#ffffff" : "#000000";
  const secondaryTextColor = prefersDarkMode ? "rgba(235, 235, 245, 0.6)" : "rgba(60, 60, 67, 0.6)";
  const borderColor = prefersDarkMode ? "rgba(84, 84, 88, 0.65)" : "rgba(0, 0, 0, 0.1)";

  return (
    <Box sx={{ px: 2, py: 2.5 }}>
      {/* 页面标题 */}
      <Typography
        variant="h4"
        sx={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: textColor,
          mb: 3,
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
        }}
      >
        认识我们
      </Typography>

      {/* 内容区域 */}
      {introSections.map((section, index) => (
        <SectionContainer key={section.id} id={`section-${section.id}`}>
          {/* 章节标题 */}
          <Typography
            variant="h5"
            sx={{
              fontSize: "1.1rem",
              fontWeight: 600,
              color: textColor,
              mb: 1.5,
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
            }}
          >
            {section.title}
          </Typography>

          {/* 章节内容 */}
          <Box>
            {section.descriptions.map((description, descIndex) => (
              <Typography
                key={descIndex}
                sx={{
                  fontSize: "0.9rem",
                  color: textColor,
                  lineHeight: 1.6,
                  mb: 1.5,
                  fontWeight: 400,
                }}
              >
                {description}
              </Typography>
            ))}
          </Box>

          {/* 分隔线（最后一个section不显示） */}
          {index < introSections.length - 1 && (
            <Divider
              sx={{
                mt: 2.5,
                borderColor: borderColor,
              }}
            />
          )}
        </SectionContainer>
      ))}
    </Box>
  );
};

export default MobileIntro;
